// app/src/simulations/buildContributeV2Ix.ts
import {
  PublicKey,
  SystemProgram,
  TransactionInstruction,
  AccountMeta,
} from "@solana/web3.js";
import { BorshCoder, BN, type Idl } from "@coral-xyz/anchor";
import { Buffer } from "buffer";
import idlJson from "../idl/weare_susu.json";

/**
 * Build contribute_v2 TransactionInstruction from the IDL (Option A).
 *
 * IDL accounts (confirmed):
 *   wallet (signer,writable)
 *   pool_v2 (writable)
 *   member_v2 (PDA,writable) seeds: ["member_v2", pool_v2, wallet]
 *   vault_v2  (PDA,writable) seeds: ["vault_v2",  pool_v2]
 *   system_program (11111111111111111111111111111111)
 *
 * Args:
 *   lamports: u64
 */

function assertU64String(name: string, v: string) {
  const s = String(v ?? "").trim();
  if (!/^\d+$/.test(s)) {
    throw new Error(`${name} must be a base-10 integer string (u64). Got: '${v}'`);
  }
  const bn = new BN(s, 10);
  const max = new BN("18446744073709551615", 10);
  if (bn.gt(max)) {
    throw new Error(`${name} exceeds u64 max. Got: ${s}`);
  }
  return bn;
}

function idlSigner(acct: any): boolean {
  return Boolean(acct?.isSigner ?? acct?.signer ?? false);
}
function idlWritable(acct: any): boolean {
  return Boolean(acct?.isMut ?? acct?.writable ?? false);
}

function findIx(idl: Idl, nameA: string, nameB: string) {
  return idl.instructions.find((ix) => ix.name === nameA) ?? idl.instructions.find((ix) => ix.name === nameB);
}

function findAccountDef(ixDef: any, name: string) {
  return (ixDef.accounts || []).find((a: any) => a.name === name);
}

function pdaSeedConstAsciiFromIdlValueBytes(bytes: number[]): string {
  // your IDL seed const is byte array of ASCII; convert to string
  return Buffer.from(bytes).toString("utf8");
}

function deriveMemberV2Pda(programId: PublicKey, poolV2: PublicKey, wallet: PublicKey): PublicKey {
  // IDL: "member_v2" const + pool_v2 + wallet
  return PublicKey.findProgramAddressSync(
    [Buffer.from("member_v2"), poolV2.toBuffer(), wallet.toBuffer()],
    programId
  )[0];
}

function deriveVaultV2Pda(programId: PublicKey, poolV2: PublicKey): PublicKey {
  // IDL: "vault_v2" const + pool_v2
  return PublicKey.findProgramAddressSync(
    [Buffer.from("vault_v2"), poolV2.toBuffer()],
    programId
  )[0];
}

export function buildContributeV2Instruction(input: {
  programId: PublicKey;

  wallet: PublicKey;
  pool_v2: PublicKey;

  /**
   * Optional: if omitted, we derive from PDA seeds (recommended).
   * If provided, we validate against the derived PDA and fail if mismatch.
   */
  member_v2?: PublicKey;

  /**
   * Optional: if omitted, we derive from PDA seeds (recommended).
   * If provided, we validate against the derived PDA and fail if mismatch.
   */
  vault_v2?: PublicKey;

  lamports: string;
}): TransactionInstruction {
  const idl = idlJson as unknown as Idl;

  const ixDef = findIx(idl, "contribute_v2", "contributeV2");
  if (!ixDef) throw new Error("buildContributeV2Instruction: contribute_v2 not found in IDL.");

  // Validate IDL has the expected account surface (contract governance guardrail)
  const acctWallet = findAccountDef(ixDef, "wallet");
  const acctPool = findAccountDef(ixDef, "pool_v2");
  const acctMember = findAccountDef(ixDef, "member_v2");
  const acctVault = findAccountDef(ixDef, "vault_v2");
  const acctSys = findAccountDef(ixDef, "system_program") || findAccountDef(ixDef, "systemProgram");

  if (!acctWallet || !acctPool || !acctMember || !acctVault || !acctSys) {
    throw new Error(
      "buildContributeV2Instruction: IDL accounts for contribute_v2 are missing expected entries. " +
        "Re-check app/src/idl/weare_susu.json."
    );
  }

  // Encode args via IDL coder
  const coder = new BorshCoder(idl);
  const lamportsBn = assertU64String("lamports", input.lamports);

  const data = coder.instruction.encode(ixDef.name, {
    lamports: lamportsBn,
  });

  // Derive PDAs per IDL seeds (and validate if caller provided)
  const derivedMember = deriveMemberV2Pda(input.programId, input.pool_v2, input.wallet);
  const derivedVault = deriveVaultV2Pda(input.programId, input.pool_v2);

  const memberPk = input.member_v2 ?? derivedMember;
  const vaultPk = input.vault_v2 ?? derivedVault;

  if (input.member_v2 && !input.member_v2.equals(derivedMember)) {
    throw new Error(
      `member_v2 mismatch.\n` +
        `- provided: ${input.member_v2.toBase58()}\n` +
        `- derived:  ${derivedMember.toBase58()}\n` +
        `Fix the pasted PDA or omit member_v2 so we derive it.`
    );
  }

  if (input.vault_v2 && !input.vault_v2.equals(derivedVault)) {
    throw new Error(
      `vault_v2 mismatch.\n` +
        `- provided: ${input.vault_v2.toBase58()}\n` +
        `- derived:  ${derivedVault.toBase58()}\n` +
        `Fix the pasted PDA or omit vault_v2 so we derive it.`
    );
  }

  // Build account metas in exact IDL order using IDL flags
  const keys: AccountMeta[] = [];

  for (const acct of ixDef.accounts as any[]) {
    const isSigner = idlSigner(acct);
    const isWritable = idlWritable(acct);

    switch (acct.name) {
      case "wallet":
        keys.push({ pubkey: input.wallet, isSigner, isWritable });
        break;

      case "pool_v2":
        keys.push({ pubkey: input.pool_v2, isSigner, isWritable });
        break;

      case "member_v2":
        keys.push({ pubkey: memberPk, isSigner, isWritable });
        break;

      case "vault_v2":
        keys.push({ pubkey: vaultPk, isSigner, isWritable });
        break;

      case "system_program":
      case "systemProgram":
        keys.push({ pubkey: SystemProgram.programId, isSigner: false, isWritable: false });
        break;

      default:
        throw new Error(
          `buildContributeV2Instruction: Unhandled account '${acct.name}'. ` +
            `Update buildContributeV2Ix.ts mapping to match the IDL.`
        );
    }
  }

  return new TransactionInstruction({
    programId: input.programId,
    keys,
    data,
  });
}
